import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Input } from "../components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  ArrowLeft,
  Calendar,
  Check,
  Copy,
  Download,
  Loader2,
  RefreshCw,
  Smartphone,
  Monitor,
  CheckCircle2,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";

// Production domain for calendar URLs
const PRODUCTION_DOMAIN = "https://usetally.ca";

export const CalendarSettingsPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [icsFeed, setIcsFeed] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [billsCount, setBillsCount] = useState(0);

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const [icsRes, billsRes, creditCardsRes] = await Promise.all([
        api.get("/calendar/ics/feed"),
        api.get("/bills/upcoming"),
        api.get("/credit-cards/bills")
      ]);
      setIcsFeed(icsRes.data);
      // Count both bills and all credit cards (matching the bills page)
      const billsCount = billsRes.data?.length || 0;
      const creditCardsCount = creditCardsRes.data?.length || 0;
      setBillsCount(billsCount + creditCardsCount);
    } catch (error) {
      console.error("Failed to fetch calendar data:", error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  // Get production URLs for calendar feeds
  const getProductionUrl = (url) => {
    if (!url) return "";
    // Replace any domain (including webcal://) with production domain
    // Handle both http(s):// and webcal:// protocols
    if (url.startsWith("webcal://")) {
      return url.replace(/webcal:\/\/[^/]+/, "webcal://usetally.ca");
    }
    return url.replace(/https?:\/\/[^/]+/, PRODUCTION_DOMAIN);
  };

  const handleCopyLink = async (url) => {
    try {
      const productionUrl = getProductionUrl(url);
      await navigator.clipboard.writeText(productionUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleDownloadICS = () => {
    if (icsFeed?.feed_url) {
      window.open(getProductionUrl(icsFeed.feed_url), "_blank");
    }
  };

  const handleOpenWebcal = () => {
    if (icsFeed?.webcal_url) {
      const productionUrl = getProductionUrl(icsFeed.webcal_url);
      window.location.href = productionUrl;
    }
  };

  const handleRegenerateToken = async () => {
    setRegenerating(true);
    try {
      const response = await api.post("/calendar/ics/regenerate");
      setIcsFeed(response.data);
      toast.success("Feed link regenerated");
    } catch (error) {
      toast.error("Failed to regenerate link");
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-2xl" data-testid="calendar-settings-loading">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-48 rounded-2xl" />
        <Skeleton className="h-48 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="calendar-settings-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate("/settings")}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Sync Bills to Calendar</h1>
          <p className="text-muted-foreground mt-1">
            Never miss a bill payment again
          </p>
        </div>
      </div>

      {/* Bills count info */}
      <Card className="border-primary/20 bg-primary/[0.03]">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-semibold">{billsCount} upcoming bill{billsCount !== 1 ? "s" : ""} ready to sync</p>
            <p className="text-sm text-muted-foreground">
              Your calendar will automatically update when bills change
            </p>
          </div>
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
        </CardContent>
      </Card>

      {/* Main Calendar Subscription Card */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Subscribe to Your Bills Calendar
          </CardTitle>
          <CardDescription>
            Works with iPhone, Android, Google Calendar, Outlook, and any calendar app
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Device-specific instructions */}
          <Tabs defaultValue="iphone" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="iphone" className="gap-2">
                <Smartphone className="h-4 w-4" />
                iPhone
              </TabsTrigger>
              <TabsTrigger value="android" className="gap-2">
                <Smartphone className="h-4 w-4" />
                Android
              </TabsTrigger>
              <TabsTrigger value="desktop" className="gap-2">
                <Monitor className="h-4 w-4" />
                Desktop
              </TabsTrigger>
            </TabsList>
            
            {/* iPhone Instructions */}
            <TabsContent value="iphone" className="space-y-4 pt-4">
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Add to iPhone Calendar:</h4>
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">1</span>
                    <span>Tap the button below to open in Calendar app</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">2</span>
                    <span>Tap &quot;Subscribe&quot; when prompted</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">3</span>
                    <span>Your bills will appear as all-day events with reminders</span>
                  </li>
                </ol>
              </div>
              <Button 
                onClick={handleOpenWebcal}
                className="w-full gap-2"
                data-testid="subscribe-iphone-btn"
              >
                <Calendar className="h-4 w-4" />
                Open in iPhone Calendar
                <ChevronRight className="h-4 w-4 ml-auto" />
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Or copy the link below and add it manually in Settings → Calendar → Accounts → Add Subscribed Calendar
              </p>
            </TabsContent>

            {/* Android Instructions */}
            <TabsContent value="android" className="space-y-4 pt-4">
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Add to Google Calendar (Android):</h4>
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">1</span>
                    <span>Copy the calendar link below</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">2</span>
                    <span>Open <strong>Google Calendar</strong> on your computer (calendar.google.com)</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">3</span>
                    <span>Click the + next to &quot;Other calendars&quot; → &quot;From URL&quot;</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">4</span>
                    <span>Paste the link and click &quot;Add calendar&quot;</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">5</span>
                    <span>It will sync to your Android phone automatically</span>
                  </li>
                </ol>
              </div>
              <div className="flex gap-2">
                <Input
                  value={getProductionUrl(icsFeed?.feed_url) || ""}
                  readOnly
                  className="font-mono text-xs flex-1"
                  data-testid="ics-feed-url-android"
                />
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => handleCopyLink(icsFeed?.feed_url || "")}
                  data-testid="copy-android-btn"
                >
                  {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </TabsContent>

            {/* Desktop Instructions */}
            <TabsContent value="desktop" className="space-y-4 pt-4">
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Add to any calendar app:</h4>
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">1</span>
                    <span>Copy the calendar subscription URL below</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">2</span>
                    <span>In your calendar app, look for &quot;Subscribe to calendar&quot; or &quot;Add calendar from URL&quot;</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">3</span>
                    <span>Paste the URL and subscribe</span>
                  </li>
                </ol>
              </div>
              <div className="flex gap-2">
                <Input
                  value={getProductionUrl(icsFeed?.webcal_url) || ""}
                  readOnly
                  className="font-mono text-xs flex-1"
                  data-testid="ics-feed-url-desktop"
                />
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => handleCopyLink(icsFeed?.webcal_url || "")}
                  data-testid="copy-desktop-btn"
                >
                  {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleDownloadICS}
                  className="gap-2"
                  data-testid="download-ics-btn"
                >
                  <Download className="h-4 w-4" />
                  Download .ics file
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          {/* Regenerate option */}
          <div className="pt-4 border-t border-border/50">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="gap-2 text-muted-foreground"
                  data-testid="regenerate-ics-btn"
                >
                  <RefreshCw className="h-4 w-4" />
                  Generate new link (invalidates old link)
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Regenerate calendar link?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will invalidate the current link. You&apos;ll need to re-subscribe in your calendar app with the new link.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleRegenerateToken}
                    disabled={regenerating}
                  >
                    {regenerating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Regenerating...
                      </>
                    ) : (
                      "Regenerate"
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium text-sm">How often does it update?</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Most calendar apps refresh subscribed calendars every few hours. When you add or change bills in Tally, the updates will appear in your calendar within 6-12 hours.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-sm">Will I get reminders?</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Yes! Each bill includes a reminder 1 day before it&apos;s due. You can also customize notifications in your calendar app settings.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-sm">What bills are included?</h4>
            <p className="text-sm text-muted-foreground mt-1">
              All your upcoming bills for the next 3 months are included. Both bills detected from your bank and manually added bills will appear.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
